import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { productCategories } from '@data/categories'   // ← only change: import real data

import img2 from '@assets/images/hero/hero-bg-02.webp'
import img4 from '@assets/images/hero/hero-bg-04.webp'
import img6 from '@assets/images/hero/hero-bg-06.webp'
import img7 from '@assets/images/hero/hero-bg-cow.jpg'

const IMAGES = [img2, img4, img6, img7]
const SLIDES = [...IMAGES, IMAGES[0]]
const TOTAL  = SLIDES.length

export default function HeroSection() {
  const [vis, setVis] = useState(false)
  useEffect(() => {
    const t = setTimeout(() => setVis(true), 100)
    return () => clearTimeout(t)
  }, [])

  const [index, setIndex] = useState(0)
  const stripRef          = useRef(null)
  const isSnapping        = useRef(false)

  useEffect(() => {
    const id = setInterval(() => setIndex(prev => prev + 1), 4000)
    return () => clearInterval(id)
  }, [])

  useEffect(() => {
    const strip = stripRef.current
    if (!strip) return

    if (isSnapping.current) {
      isSnapping.current = false
      return
    }

    strip.style.transition = 'transform 0.7s ease-in-out'
    strip.style.transform  = `translateX(calc(-${index} * 100% / ${TOTAL}))`

    if (index === TOTAL - 1) {
      const onEnd = () => {
        strip.removeEventListener('transitionend', onEnd)
        strip.style.transition = 'none'
        strip.style.transform  = 'translateX(0)'
        isSnapping.current     = true
        setIndex(0)
      }
      strip.addEventListener('transitionend', onEnd)
    }
  }, [index])

  const OVERLAP = 'clamp(120px, 18vw, 220px)'

  return (
    <section style={{ position: 'relative', width: '100%', overflow: 'hidden',background: '#ffffff' }}>

      {/* ── Carousel strip ── */}
      <div style={{
        width: '100%', height: 'clamp(220px, 48vw, 480px)',
        position: 'relative', overflow: 'hidden', background: '#1a3028',
      }}>
        <div
          ref={stripRef}
          style={{
            display: 'flex',
            width:   `${TOTAL * 100}%`,
            height:  '100%',
          }}
        >
          {SLIDES.map((src, i) => (
            <img
              key={i}
              src={src}
              alt=""
              style={{
                width:      `calc(100% / ${TOTAL})`,
                height:     '100%',
                objectFit:  'cover',
                flexShrink: 0,
              }}
            />
          ))}
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════
          BOTTOM STRIP — overlaps image on desktop
          On mobile: sits below image, full width, stacked
      ═══════════════════════════════════════════════════ */}
      <div
        className="hero-strip"
        style={{
          display:    'flex',
          position:   'relative',
          zIndex:     10,
          opacity:    vis ? 1 : 0,
          transform:  vis ? 'translateY(0)' : 'translateY(10px)',
          transition: 'all 0.6s ease 0.15s',
        }}
      >
        {/* ── Green content box ── */}
        <div
          className="hero-green-box"
          style={{
            background: '#007935',
            padding:    'clamp(24px, 3.5vw, 48px) clamp(20px, 3.5vw, 52px)',
            flex:       '1 1 auto',
            minWidth:   0,
            display:    'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <h1 style={{
            fontFamily:    "'DM Sans', sans-serif",
            fontSize:      'clamp(1.75rem, 4.5vw, 4rem)',
            fontWeight:    300,
            color:         '#ffffff',
            lineHeight:    1.15,
            margin:        '0 0 16px',
            letterSpacing: '.05em',
          }}>
            The science of <br></br>livestock<br />nutrition.
          </h1>
          <p style={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize:   'clamp(13px, 1.3vw, 15px)',
            fontWeight: 400,
            color:      'rgba(255,255,255,0.85)',
            lineHeight: 1.7,
            margin:     '0',
            maxWidth:   '420px',
          }}>
            Connecting global innovation with local application - premium nutritional
            solutions for poultry, dairy, and swine production systems across Sri Lanka.
            We empower farmers to achieve higher productivity and sustainable practices.
          </p>
        </div>

        {/* ── Category list — now from real categories.js ── */}
        <div
          className="hero-cat-col"
          style={{
            background:     '#007935',
            borderLeft:     '1px none rgba(255,255,255,0.15)',
            display:        'flex',
            flexDirection:  'column',
            justifyContent: 'center',
            padding:        ' clamp(10px, 1.5vw, 20px)',
            flexShrink:     0,
            width:          'clamp(160px, 22vw, 280px)',
          }}
        >
          {productCategories.map((cat, i) => (
            <Link
              key={cat.id}
              to={`/products?category=${cat.id}`}
              style={{
                display:        'flex',
                alignItems:     'center',
                justifyContent: 'flex-end',
                padding:        'clamp(8px, 1.2vw, 13px) clamp(14px, 2.5vw, 28px) clamp(8px, 1.2vw, 13px) clamp(14px, 2.5vw, 28px)',
                fontFamily:     "'DM Sans', sans-serif",
                fontSize:       'clamp(15px, 1.5vw, 25px)',
                fontWeight:     400,
                color:          'rgba(255,255,255,0.90)',
                textDecoration: 'none',
                textAlign:      'right',
                borderBottom:   i < productCategories.length - 1
                  ? '1px solid rgba(255,255,255,0.55)' : 'none',
                transition:     'color 0.2s ease, transform 0.2s ease',
                whiteSpace:     'nowrap',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.color     = '#ffffff'
                e.currentTarget.style.transform = 'scale(1.06)'
              }}
              onMouseLeave={e => {
                e.currentTarget.style.color     = 'rgba(255,255,255,0.90)'
                e.currentTarget.style.transform = 'scale(1)'
              }}
            >
              {cat.label}
            </Link>
          ))}
        </div>
      </div>

      <style>{`

        /* ── DESKTOP: strip overlaps image bottom ── */
        @media (min-width: 768px) {
          .hero-strip {
            margin-top: calc(-1 * clamp(120px, 18vw, 220px));
            margin-right: clamp(0px, 28vw, 32%);
          }
          .hero-green-box {
            /* normal layout */
          }
          .hero-cat-col {
            display: flex !important;
            flex-direction: column;
            width: clamp(160px, 22vw, 280px);
          }
        }

        /* ── TABLET ── */
        @media (min-width: 768px) and (max-width: 1023px) {
          .hero-strip {
            margin-right: clamp(0px, 15vw, 18%);
          }
          .hero-cat-col {
            width: clamp(140px, 18vw, 220px) !important;
          }
        }

        /* ── MOBILE: MIAVIT mobile layout ── */
        @media (max-width: 767px) {
          .hero-strip {
            /* No negative margin — sits below image */
            margin-top: 0 !important;
            margin-right: 0 !important;
            /* Stack vertically */
            flex-direction: column !important;
          }

          .hero-green-box {
            /* Full width text box */
            width: 100%;
            padding: 24px 20px !important;
          }

          .hero-green-box h1 {
            font-size: 1.75rem !important;
            margin-bottom: 12px !important;
          }

          .hero-green-box p {
            font-size: 13px !important;
          }

          /* Category list — stacks below text box on mobile */
          /* Stays green, right-aligned, full width */
          .hero-cat-col {
            display: flex !important;
            flex-direction: column !important;
            width: 100% !important;
            border-left: none !important;
            border-top: 1px solid rgba(255,255,255,0.15) !important;
          }

          .hero-cat-col a {
            /* On mobile: text LEFT aligned like MIAVIT mobile */
            justify-content: flex-start !important;
            text-align: left !important;
            padding: 12px 20px !important;
            font-size: 14px !important;
            white-space: normal !important;
          }
        }

        /* ── Very small phones ── */
        @media (max-width: 380px) {
          .hero-green-box { padding: 20px 16px !important; }
          .hero-green-box h1 { font-size: 1.5rem !important; }
        }
      `}</style>
    </section>
  )
}